import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { DwComponent, DwComponentType, DwContext, DwFormControl, DwFormFieldConfig } from '@devwareapps/devware-cap';
import * as luxon from 'luxon';
import { Subscription } from 'rxjs';

interface ExpirationDateTimeControlOptions {
  warningDays?: number | string;
  dateFormat?: string;
}

interface ExpirationDateTimeDisplay {
  readonly dateText: string;
  readonly chipText: string;
  readonly tone: 'none' | 'warning' | 'expired';
}
 
@DwComponent({
  key: 'expiration-date-time-readonly-form-control',
  name: 'Expiration Date/Time Readonly Form Control',
  componentType: DwComponentType.formControl,
  isGlobal: true
})  
@Component({
  selector: 'app-expiration-date-time-readonly-form-control',
  templateUrl: './expiration-date-time-readonly-form-control.component.html',
  styleUrls: ['./expiration-date-time-readonly-form-control.component.scss']
})
export class ExpirationDateTimeReadonlyFormControlComponent implements DwFormControl, OnInit, OnDestroy {
  fieldDef: DwFormFieldConfig;
  formGroup: FormGroup;
  inputClass = 'form-control'; 
  @Input() context: DwContext;

  display: ExpirationDateTimeDisplay = {
    dateText: 'None',
    chipText: '',
    tone: 'none'
  };

  private valueChangesSubscription: Subscription;

  ngOnInit(): void {
    this.updateDisplay(this.control?.value);

    this.valueChangesSubscription = this.control?.valueChanges.subscribe(value => {
      this.updateDisplay(value);
    });
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  get control(): AbstractControl {
    return this.formGroup?.get(this.fieldDef?.attrName);
  }

  private updateDisplay(value: any): void {
    const expirationDateTime = this.parseDateTime(value);

    if (!expirationDateTime) {
      this.display = {
        dateText: value ? `${value}` : 'None',
        chipText: '',
        tone: 'none'
      };
      return;
    }

    const options = this.resolveOptions();
    const warningDays = this.resolveWarningDays(options.warningDays);
    const today = luxon.DateTime.local().startOf('day');
    const expirationDay = expirationDateTime.startOf('day');
    const daysUntilExpiration = Math.round(expirationDay.diff(today, 'days').days);

    this.display = {
      dateText: expirationDateTime.toFormat(options.dateFormat || 'MM/dd/yyyy'),
      ...this.getExpirationStatus(daysUntilExpiration, warningDays)
    };
  }

  private getExpirationStatus(
    daysUntilExpiration: number,
    warningDays: number
  ): Pick<ExpirationDateTimeDisplay, 'chipText' | 'tone'> {
    if (daysUntilExpiration < 0) {
      const daysExpired = Math.abs(daysUntilExpiration);

      return {
        chipText: `Expired ${daysExpired} ${daysExpired === 1 ? 'day' : 'days'} ago`,
        tone: 'expired'
      };
    }

    if (daysUntilExpiration > warningDays) {
      return { chipText: '', tone: 'none' };
    }

    return {
      chipText: daysUntilExpiration < 1
        ? 'Expires today'
        : `Expires in ${daysUntilExpiration} ${daysUntilExpiration === 1 ? 'day' : 'days'}`,
      tone: 'warning'
    };
  }

  private parseDateTime(value: any): luxon.DateTime | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      const dateTimeFromDate = luxon.DateTime.fromJSDate(value);

      return dateTimeFromDate.isValid ? dateTimeFromDate : null;
    }

    if (typeof value === 'number') {
      const dateTimeFromNumber = luxon.DateTime.fromMillis(value);

      return dateTimeFromNumber.isValid ? dateTimeFromNumber : null;
    }

    const valueText = `${value}`.trim();
    const isoDateTime = luxon.DateTime.fromISO(valueText, { setZone: true });

    if (isoDateTime.isValid) {
      return isoDateTime.toLocal();
    }

    const standardDateTime = luxon.DateTime.fromFormat(valueText, 'MM/dd/yyyy hh:mm a');

    if (standardDateTime.isValid) {
      return standardDateTime;
    }

    const dateOnly = luxon.DateTime.fromFormat(valueText, 'MM/dd/yyyy');

    return dateOnly.isValid ? dateOnly : null;
  }

  private resolveOptions(): ExpirationDateTimeControlOptions {
    const fieldDef = this.fieldDef as any;

    return {
      ...(fieldDef?.config || fieldDef?.componentConfig || fieldDef?.configData || {}),
      ...(fieldDef?.controlOptions || {})
    };
  }

  private resolveWarningDays(value?: number | string): number {
    if (value === null || value === undefined || value === '') {
      return 30;
    }

    const parsedValue = Number(value);

    return isNaN(parsedValue) ? 30 : Math.max(0, parsedValue);
  }
}
